import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post('https://land-survey-main-server.onrender.com/forgotPassword', { user_email: email });
      
      if (response?.data?.status === true) {
        setSuccessMessage(response.data.message);
        setIsSubmitted(true);
      } else {
        setError(response?.data?.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="flex items-center justify-center min-h-screen px-4 bg-gray-50 sm:px-6 lg:px-8">
        <div className="w-full max-w-md p-8 space-y-8 bg-white border border-gray-100 shadow-xl rounded-2xl">
          
          {!isSubmitted ? (
            <>
              {/* Header */}
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 mx-auto text-indigo-600 bg-indigo-100 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.011 5.891L9.75 17.25H7.5v-2.25l1.359-1.359a6 6 0 0 1 5.891-7.011ZM10.5 10.5a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
                  </svg>
                </div>
                <h2 className="mt-4 text-3xl font-extrabold text-gray-900">
                  Forgot password?
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  No worries, we'll send you reset instructions.
                </p>
              </div>

              {/* Form */}
              <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                {error && (
                  <div className="p-3 text-sm text-red-600 border border-red-200 rounded-lg bg-red-50">
                    {error}
                  </div>
                )}
                
                <div>
                  <label htmlFor="email-address" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <input
                    id="email-address"
                    name="email"
                    required
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full px-3 py-2 mt-1 placeholder-gray-400 transition duration-150 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending link...
                      </span>
                    ) : (
                      'Reset password'
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="py-4 text-center">
              <div className="flex items-center justify-center w-12 h-12 mx-auto text-green-600 bg-green-100 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
              </div>
              <h2 className="mt-4 text-3xl font-extrabold text-gray-900">
                Check your email
              </h2>
              <p className="max-w-sm mx-auto mt-2 text-sm text-gray-600">
                {successMessage}
              </p>
              <button
                onClick={() => setIsSubmitted(false)}
                className="mt-6 text-sm font-medium text-indigo-600 transition duration-150 hover:text-indigo-500 focus:outline-none"
              >
                Didn't receive an email? Click to try again
              </button>
            </div>
          )}

          {/* Back to Login Link */}
          <div className="mt-4 text-center">
            <Link to="/login" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition duration-150 hover:text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
              </svg>
              Back to log in
            </Link>
          </div>

        </div>
      </div>
      <Footer />
    </>
  );
}